import { CheckOutlined } from "@ant-design/icons";
import {
  AI_QUOTA_PAYWALL_CONTACT_SALES_EMAIL,
  AI_QUOTA_PAYWALL_CONTACT_SALES_MAILTO,
  AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES,
  AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES,
  AI_QUOTA_PAYWALL_REASON_CONFIG,
  AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL,
  AI_QUOTA_PAYWALL_TEAMS_FEATURES,
  AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES,
} from "@superblocksteam/shared";
import { Col, Modal, Row, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "components/ui/Button";
import logger from "utils/logger";
import type { AiQuotaPaywallReason as AiQuotaReason } from "@superblocksteam/shared";

type AiQuotaPaywallModalProps = {
  open: boolean;
  reason: AiQuotaReason | undefined;
  onClose: () => void;
};

const Header = styled.div`
  text-align: center;
  margin-bottom: 13px;
`;

const HeaderSubtitle = styled(Typography.Text)`
  display: block;
  color: ${(props) => props.theme.colors.GREY_500};
  font-size: 10px;
  line-height: 1.45;
  margin-top: 4px;
`;

const ReasonText = styled(Typography.Text)`
  display: block;
  color: ${(props) => props.theme.colors.GREY_900};
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
`;

const PlanCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 9px;
  border: 1px solid ${(props) => props.theme.colors.GREY_100};
  background: ${(props) => props.theme.colors.WHITE};
  padding: 12px;
  height: 100%;

  &[data-highlighted="true"] {
    border: 2px solid ${(props) => props.theme.colors.GREY_900};
  }
`;

const PlanTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const PlanTag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  background: ${(props) => props.theme.colors.GREY_50};
  color: ${(props) => props.theme.colors.GREY_600};
  font-size: 9px;
  font-weight: 500;
  padding: 3px 7px;
`;

const PlanPrice = styled.div`
  font-size: 33px;
  font-weight: 700;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PlanPriceUnit = styled.div`
  font-size: 10px;
  color: ${(props) => props.theme.colors.GREY_500};
  margin-top: 2px;
`;

const PlanDescription = styled.div`
  font-size: 10px;
  color: ${(props) => props.theme.colors.GREY_600};
  margin: 9px 0;
  min-height: 37px;
`;

const CTAButton = styled(Button)`
  width: 100%;
  height: 29px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 10px;
  border: 1px solid ${(props) => props.theme.colors.GREY_200};
  color: ${(props) => props.theme.colors.GREY_700};
  background: ${(props) => props.theme.colors.WHITE};

  &[data-primary="true"] {
    border: 1px solid ${(props) => props.theme.colors.GREY_900};
    color: ${(props) => props.theme.colors.WHITE};
    background: ${(props) => props.theme.colors.GREY_900};
  }
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
  margin-top: 4px;
  margin-bottom: 7px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 10px;
  color: ${(props) => props.theme.colors.GREY_600};
  margin-bottom: 5px;
`;

const ContactSalesText = styled(Typography.Text)`
  display: block;
  margin-top: -9px;
  margin-bottom: 7px;
  text-align: center;
  color: ${(props) => props.theme.colors.GREY_500};
  font-size: 9px;
`;

export const AiQuotaPaywallModal = ({
  open,
  reason,
  onClose,
}: AiQuotaPaywallModalProps) => {
  const config = reason ? AI_QUOTA_PAYWALL_REASON_CONFIG[reason] : null;
  const title = config?.title ?? "Upgrade your AI plan";

  useEffect(() => {
    if (!open) {
      return;
    }

    logger.info("[AiQuota] Paywall modal shown", {
      reason: reason ?? "unknown",
      surface: "ui",
    });
  }, [open, reason]);

  const onUpgrade = useCallback(() => {
    window.open(AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL, "_blank");
  }, []);

  return (
    <Modal
      open={open}
      closable
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={630}
      bodyStyle={{ padding: "17px" }}
      data-test="ai-quota-paywall-modal"
    >
      <Header>
        <ReasonText>{title}</ReasonText>
        <HeaderSubtitle>
          To continue building, purchase a self-serve plan for you and your team
          or contact sales to discuss enterprise pricing
        </HeaderSubtitle>
      </Header>

      <Row gutter={10}>
        <Col span={12}>
          <PlanCard data-highlighted="true">
            <PlanTopRow>
              <PlanTag>Teams</PlanTag>
            </PlanTopRow>
            <PlanPrice>$200</PlanPrice>
            <PlanPriceUnit>
              per AI builder per month when billed annually
            </PlanPriceUnit>
            <PlanDescription>
              Everything you need to build and deploy internal tools with AI —
              self-serve.
            </PlanDescription>
            <CTAButton data-primary="true" type="default" onClick={onUpgrade}>
              Checkout now
            </CTAButton>
            <SectionTitle>AI & Credits</SectionTitle>
            {AI_QUOTA_PAYWALL_TEAMS_FEATURES.map((feature) => (
              <FeatureItem key={feature}>
                <CheckOutlined
                  style={{
                    color: "#121517",
                    fontSize: 10,
                    marginTop: 2,
                  }}
                />
                <span>{feature}</span>
              </FeatureItem>
            ))}
            <SectionTitle>Platform</SectionTitle>
            {AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES.map((feature) => (
              <FeatureItem key={feature}>
                <CheckOutlined
                  style={{
                    color: "#121517",
                    fontSize: 10,
                    marginTop: 2,
                  }}
                />
                <span>{feature}</span>
              </FeatureItem>
            ))}
          </PlanCard>
        </Col>

        <Col span={12}>
          <PlanCard>
            <PlanTopRow>
              <PlanTag>Enterprise</PlanTag>
            </PlanTopRow>
            <PlanPrice>Custom</PlanPrice>
            <PlanPriceUnit>
              flexible pricing tailored to your organization
            </PlanPriceUnit>
            <PlanDescription>
              Advanced security, custom deployment, and dedicated support for
              large teams.
            </PlanDescription>
            <CTAButton
              type="default"
              onClick={() => window.open(AI_QUOTA_PAYWALL_CONTACT_SALES_MAILTO)}
            >
              Contact us
            </CTAButton>
            <ContactSalesText>
              Or email{" "}
              <Typography.Link href={AI_QUOTA_PAYWALL_CONTACT_SALES_MAILTO}>
                {AI_QUOTA_PAYWALL_CONTACT_SALES_EMAIL}
              </Typography.Link>
            </ContactSalesText>

            <SectionTitle>AI & Credits</SectionTitle>
            {AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES.map((feature) => (
              <FeatureItem key={feature}>
                <CheckOutlined
                  style={{
                    color: "#121517",
                    fontSize: 10,
                    marginTop: 2,
                  }}
                />
                <span>{feature}</span>
              </FeatureItem>
            ))}
            <SectionTitle>Platform</SectionTitle>
            {AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES.map((feature) => (
              <FeatureItem key={feature}>
                <CheckOutlined
                  style={{
                    color: "#121517",
                    fontSize: 10,
                    marginTop: 2,
                  }}
                />
                <span>{feature}</span>
              </FeatureItem>
            ))}
          </PlanCard>
        </Col>
      </Row>
    </Modal>
  );
};

export function useAiQuotaPaywall() {
  const [paywallState, setPaywallState] = useState<{
    open: boolean;
    reason: AiQuotaReason | undefined;
  }>({ open: false, reason: undefined });

  const showPaywall = useCallback((reason: AiQuotaReason | undefined) => {
    setPaywallState({ open: true, reason });
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallState({ open: false, reason: undefined });
  }, []);

  return { paywallState, showPaywall, closePaywall };
}
